import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ProfileDetail } from '@/components/profiles/profile-detail';
import { hasPermission } from '@/lib/permissions';
import { companyProfileDetailOptions } from '@/hooks/query-options';

// `research` opens Research Account on arrival (from Add Target Account).
type ProfileSearch = { research?: true };

// Company Profile detail — full-page view/edit for one profile. The `profiles_`
// suffix escapes the tabbed /actions/profiles layout (same convention as
// automations_.robots / settings_.ai.memory).
export const Route = createFileRoute(
  '/_auth/_side-layout/actions/profiles_/$profileId',
)({
  component: ProfileDetailPage,
  validateSearch: (search): ProfileSearch => ({
    research: search.research === true ? true : undefined,
  }),
  // Read-open (reports): the insights/signals/firmographics are a read surface.
  // Write controls inside are gated on `action:['create']` per control.
  beforeLoad: async ({ context }) => {
    if (!hasPermission(context.userRole, { reports: ['read'] })) {
      throw redirect({ to: '/' });
    }
  },
  // Warm the detail query. A missing/foreign id 404s — swallow it here and let
  // the component render its not-found state instead of a route-level error.
  loader: ({ context: { queryClient }, params }) =>
    queryClient
      .ensureQueryData(companyProfileDetailOptions(params.profileId))
      .catch(() => null),
});

function ProfileDetailPage() {
  const { profileId } = Route.useParams();
  const { research } = Route.useSearch();
  const navigate = useNavigate();
  return (
    <ProfileDetail
      profileId={profileId}
      researchRequested={research === true}
      onResearchClosed={() =>
        void navigate({
          to: Route.fullPath,
          params: { profileId },
          search: { research: undefined },
          replace: true,
        })
      }
    />
  );
}
